// (c) chaehongee. Confidential and proprietary.

import { BaseEdge, getBezierPath, Position, type EdgeProps } from '@xyflow/react';
import { TREE_LAYOUT_CONSTANTS } from './constants';

export function CustomEdge({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    style = {}
}: EdgeProps) {
    // Calculate the distance and direction between source and target
    const distance = Math.sqrt(
        Math.pow(targetX - sourceX, 2) + Math.pow(targetY - sourceY, 2)
    );
    const verticalDistance = Math.abs(targetY - sourceY);

    // Calculate dynamic distance threshold based on layout constants
    // A long distance is when the edge spans more than 1.5 rank separations
    const longDistanceThreshold = TREE_LAYOUT_CONSTANTS.RANKSEP * 1.5 + TREE_LAYOUT_CONSTANTS.NODE_SIZE;
    const isLongDistance = distance > longDistanceThreshold;
    const isMainlyVertical = verticalDistance > Math.abs(targetX - sourceX) * 1.2;

    let edgePath: string;

    if (isLongDistance && isMainlyVertical) {
        // For long vertical connections, create a hybrid path:
        // 1. Goes straight down for 80% of the journey  
        // 2. Uses React Flow's proper bezier curve for the final 20%
        const straightLineRatio = 0.8; // Go straight for 80% of the distance
        const straightEndY = sourceY + (targetY - sourceY) * straightLineRatio;

        // Create a proper bezier curve for the final approach using getBezierPath
        const [curvedPath] = getBezierPath({
            sourceX: sourceX,
            sourceY: straightEndY,
            sourcePosition: Position.Bottom,
            targetX,
            targetY,
            targetPosition: Position.Top,
            curvature: 0.25 // Moderate curvature for smooth final approach
        });

        // Combine straight line with proper bezier curve
        // Remove the 'M' (move) command from bezier path and replace with our starting point
        const bezierPathWithoutMove = curvedPath.substring(curvedPath.indexOf(' ') + 1);
        edgePath = `M ${sourceX} ${sourceY} L ${sourceX} ${straightEndY} ${bezierPathWithoutMove}`;
    } else {
        // For all other distances, use React Flow's getBezierPath
        [edgePath] = getBezierPath({
            sourceX,
            sourceY,
            sourcePosition: Position.Bottom,
            targetX,
            targetY,
            targetPosition: Position.Top,
            curvature: isLongDistance ? 0.3 : 0.2
        });
    }

    return (
        <BaseEdge
            id={id}
            path={edgePath}
            style={{
                stroke: 'var(--tree-node-border)',
                strokeWidth: 0.5,
                strokeLinecap: 'round',
                ...style,
            }}
        />
    );
}
